import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BookOpen, CalendarDays, Star, Users, Video, MessageSquare, AlertTriangle, Loader2, ChevronRight, ShieldAlert, HeartPulse } from 'lucide-react';
import { minutesUntilStart, formatLocalTime } from '../../lib/session-time';
import { formatDate, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const CARD_DEFS = {
  group_batches: { title: 'Group Batches', description: 'All group batches assigned to you', api: { path: '/api/batches', resultKey: 'batches', filter: (b) => b.batch_type === 'group' }, type: 'batch' },
  one_on_one_batches: { title: '1:1 Batches', description: 'Private consultation batches', api: { path: '/api/batches', resultKey: 'batches', filter: (b) => b.batch_type === 'one_on_one' }, type: 'batch' },
  upcoming_sessions: { title: 'Upcoming Sessions', description: 'Your next scheduled classes', api: { path: '/api/sessions?status=upcoming&limit=100', resultKey: 'sessions' }, type: 'session' },
  missed_sessions: { title: 'Missed Sessions', description: 'Sessions you did not start within the buffer window', api: { path: '/api/sessions?status=missed&limit=100', resultKey: 'sessions' }, type: 'session' },
};

export default function InstructorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [todaySessions, setTodaySessions] = useState([]);
  const [weekSessions, setWeekSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRatings, setMyRatings] = useState({ ratings: [], avg_rating: 0, total_reviews: 0 });
  const [bufferSettings, setBufferSettings] = useState({ instructor_start_buffer: 10, learner_join_buffer: 5 });
  const [scheduleView, setScheduleView] = useState('today'); // 'today' | 'week'
  const [modal, setModal] = useState(null); // {scope, title, description, items, loading}
  const [brief, setBrief] = useState(null); // {name, loading, data, error}

  const openBrief = async (sessionId, learnerId, name) => {
    setBrief({ name, loading: true });
    try {
      const res = await api.get(`/api/instructor/sessions/${sessionId}/learners/${learnerId}/brief`);
      setBrief({ name: res.data.name || name, loading: false, data: res.data });
    } catch (err) {
      setBrief({ name, loading: false, error: err.response?.data?.detail || 'Could not load brief' });
    }
  };

  useEffect(() => {
    Promise.all([
      api.get('/api/dashboard/stats'),
      api.get('/api/instructor/today-sessions'),
      api.get('/api/feedback/instructor/my-ratings'),
      api.get('/api/settings/buffers'),
    ]).then(([statsRes, todayRes, ratingsRes, bufferRes]) => {
      setStats(statsRes.data);
      setTodaySessions(todayRes.data.sessions || []);
      setMyRatings(ratingsRes.data);
      setBufferSettings(bufferRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load weekly schedule when user switches to 'week' view (once)
  useEffect(() => {
    if (scheduleView !== 'week' || weekSessions.length > 0) return;
    const today = new Date();
    const weekOut = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const toIST = (d) => new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000).toISOString().slice(0, 10);
    api.get('/api/sessions', { params: { status: 'upcoming', date_from: toIST(today), date_to: toIST(weekOut), limit: 100 } })
      .then((res) => setWeekSessions(res.data.sessions || []))
      .catch(() => toast.error('Failed to load weekly schedule'));
  }, [scheduleView, weekSessions.length]);

  // V14.1 — Today sessions sorted by time (earliest first)
  const todayFiltered = todaySessions.slice().sort(
    (a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || '')
  );

  // Poll every 30s for live session updates
  useEffect(() => {
    const hasUpcoming = todaySessions.some(s => s.status === 'upcoming' || s.status === 'live');
    if (!hasUpcoming) return;
    const interval = setInterval(() => {
      api.get('/api/instructor/today-sessions').then(res => setTodaySessions(res.data.sessions || []));
    }, 30000);
    return () => clearInterval(interval);
  }, [todaySessions]);

  const handleStartClass = async (session) => {
    try {
      const res = await api.get(`/api/sessions/${session.id}/link`);
      if (res.data.zoom_start_url) {
        window.open(res.data.zoom_start_url, '_blank');
        toast.info('Opening Zoom... Session will show as Live once you join.');
      } else {
        toast.error(res.data.start_blocked_reason || 'Cannot start yet. Check the start buffer time in settings.');
      }
    } catch { toast.error('Failed to get session link'); }
  };

  const openDetailModal = async (scope) => {
    const def = CARD_DEFS[scope];
    if (!def) return;
    setModal({ scope, title: def.title, description: def.description, type: def.type, loading: true, items: [] });
    try {
      const res = await api.get(def.api.path);
      let items = res.data?.[def.api.resultKey] || res.data || [];
      if (def.api.filter) items = items.filter(def.api.filter);
      setModal((m) => m && m.scope === scope ? { ...m, loading: false, items } : m);
    } catch (err) {
      setModal((m) => m && m.scope === scope ? { ...m, loading: false, items: [], error: err.response?.data?.detail || 'Failed to load' } : m);
    }
  };

  const openRatingsModal = () => {
    setModal({
      scope: 'rating', title: 'All Ratings', description: `${myRatings.total_reviews} review${myRatings.total_reviews !== 1 ? 's' : ''} · Avg ${myRatings.avg_rating}/10`,
      type: 'rating', loading: false, items: myRatings.ratings,
    });
  };

  const activeSchedule = scheduleView === 'today' ? todaySessions : weekSessions;

  const scheduleByDate = useMemo(() => {
    if (scheduleView === 'today') return [{ date: null, items: todayFiltered }];
    const groups = new Map();
    // V14.1 — Group sessions by date, then sort each group by time (earliest first)
    const sortedWeek = weekSessions.slice().sort(
      (a, b) => (a.scheduled_date + a.scheduled_time).localeCompare(b.scheduled_date + b.scheduled_time)
    );
    for (const s of sortedWeek) {
      const d = s.scheduled_date;
      if (!groups.has(d)) groups.set(d, []);
      groups.get(d).push(s);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, items]) => ({ date, items }));
  }, [scheduleView, todayFiltered, weekSessions]);

  const groupBatches = stats.group_batches || 0;
  const oneOnOneBatches = stats.one_on_one_batches || 0;

  const statCards = [
    { key: 'group_batches', icon: BookOpen, iconClass: 'text-primary', value: groupBatches, label: 'Group Batches' },
    { key: 'one_on_one_batches', icon: Users, iconClass: 'text-emerald-600', value: oneOnOneBatches, label: '1:1 Batches' },
    { key: 'upcoming_sessions', icon: CalendarDays, iconClass: 'text-blue-600', value: stats.upcoming_sessions || 0, label: 'Upcoming Sessions' },
    { key: 'missed_sessions', icon: AlertTriangle, iconClass: stats.missed_sessions > 0 ? 'text-red-600' : 'text-muted-foreground', value: stats.missed_sessions || 0, label: 'Missed Sessions', highlight: stats.missed_sessions > 0 },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Welcome back, {user?.name?.split(' ')[0]}</h1>
        <p className="text-muted-foreground mt-1">Here's your schedule overview</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {statCards.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => openDetailModal(c.key)}
            className={`text-left rounded-2xl border bg-card hover:shadow-md hover:-translate-y-0.5 transition-all pt-5 pb-4 px-5 focus:outline-none focus:ring-2 focus:ring-primary/40 ${c.highlight ? 'border-red-200 bg-red-50/50' : ''}`}
            data-testid={`stat-card-${c.key}`}
          >
            <c.icon className={`h-5 w-5 mb-3 ${c.iconClass}`} />
            <p className={`text-2xl font-semibold ${c.highlight ? 'text-red-900' : ''}`}>{c.value}</p>
            <p className={`text-xs ${c.highlight ? 'text-red-700' : 'text-muted-foreground'}`}>{c.label}</p>
          </button>
        ))}
        <button
          type="button"
          onClick={openRatingsModal}
          className="text-left rounded-2xl border bg-card hover:shadow-md hover:-translate-y-0.5 transition-all pt-5 pb-4 px-5 focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="stat-card-rating"
        >
          <Star className="h-5 w-5 text-amber-500 mb-3" />
          <p className="text-2xl font-semibold">{myRatings.avg_rating || stats.avg_rating || '-'}<span className="text-sm text-muted-foreground font-normal">{myRatings.avg_rating ? '/10' : ''}</span></p>
          <p className="text-xs text-muted-foreground">Avg Rating ({myRatings.total_reviews} review{myRatings.total_reviews !== 1 ? 's' : ''})</p>
        </button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-heading text-xl">{scheduleView === 'today' ? "Today's Schedule" : 'This Week'}</CardTitle>
            <Tabs value={scheduleView} onValueChange={setScheduleView}>
              <TabsList data-testid="schedule-view-tabs">
                <TabsTrigger value="today" data-testid="schedule-view-today">Daily</TabsTrigger>
                <TabsTrigger value="week" data-testid="schedule-view-week">Weekly</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-16 bg-muted/40 rounded-xl animate-pulse" />)}</div>
          ) : activeSchedule.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{scheduleView === 'today' ? 'No sessions scheduled for today' : 'No sessions scheduled this week'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {scheduleByDate.map((group) => (
                <div key={group.date || 'today'}>
                  {group.date && (
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {formatDate(group.date)}
                    </p>
                  )}
                  <div className="space-y-3">
                    {group.items.map(s => {
                      const minsUntil = minutesUntilStart(s);
                      const canStart = minsUntil !== null && minsUntil <= (bufferSettings.instructor_start_buffer || 10);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setModal({ scope: 'session-detail', title: s.batch_name || s.title, description: `${formatDate(s.scheduled_date)} · ${s.scheduled_time} · ${s.duration_minutes}m`, type: 'session-detail', session: s, loading: false })}
                          className="w-full text-left flex items-center justify-between p-4 rounded-xl border hover:shadow-sm hover:bg-muted/20 transition-all"
                          data-testid={`today-session-${s.id}`}
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="text-center min-w-[50px]">
                              <p className="text-sm font-semibold">{formatLocalTime(s)}</p>
                              <p className="text-[10px] text-muted-foreground">{s.duration_minutes}m</p>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{s.batch_name || s.title}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {s.risk_level === 'high' && <Badge className="bg-rose-600 text-white border-0 text-[9px]">High Risk</Badge>}
                                <Badge variant="outline" className={`text-[10px] ${getStatusColor(s.status)}`}>
                                  {s.status === 'live' ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span> : s.status}
                                </Badge>
                                {s.booked_count > 0 && <span className="text-[10px] text-muted-foreground">{s.booked_count} booked</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {s.status === 'live' ? (
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleStartClass(s); }} data-testid={`rejoin-${s.id}`}><Video className="h-3.5 w-3.5 mr-1" />Rejoin</Button>
                            ) : s.status === 'upcoming' && canStart ? (
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleStartClass(s); }} data-testid={`start-class-${s.id}`}><Video className="h-3.5 w-3.5 mr-1" />Start Class</Button>
                            ) : s.status === 'upcoming' && minsUntil !== null && minsUntil <= (bufferSettings.instructor_start_buffer || 10) + 10 ? (
                              <Button size="sm" disabled className="opacity-40">Starting soon</Button>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Ratings Section (unchanged) */}
      {myRatings.ratings.length > 0 && (
        <Card className="rounded-2xl" data-testid="instructor-my-ratings">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" />My Ratings
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{myRatings.avg_rating}/10</span>
                <span className="text-xs text-muted-foreground">({myRatings.total_reviews})</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {myRatings.ratings.slice(0, 10).map((r, idx) => (
                <div key={r.id || idx} className="p-3 rounded-xl border hover:bg-muted/20 transition-colors" data-testid={`rating-item-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{r.batch_name}</p>
                      {r.scheduled_date && <p className="text-xs text-muted-foreground">{formatDate(r.scheduled_date)}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs font-semibold ml-1">{r.rating}/10</span>
                    </div>
                  </div>
                  {r.comments && (
                    <div className="mt-2 flex items-start gap-1.5">
                      <MessageSquare className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                      <p className="text-xs text-muted-foreground italic">"{r.comments}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail modal */}
      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="stat-detail-modal">
          <DialogHeader>
            <DialogTitle>{modal?.title}</DialogTitle>
            <DialogDescription>{modal?.description}</DialogDescription>
          </DialogHeader>
          {modal?.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : modal?.type === 'session-detail' ? (
            <SessionDetail session={modal.session} onStart={() => { handleStartClass(modal.session); setModal(null); }} canStart={minutesUntilStart(modal.session) !== null && minutesUntilStart(modal.session) <= (bufferSettings.instructor_start_buffer || 10)} onLearnerClick={(lid, name) => openBrief(modal.session.id, lid, name)} canViewBrief={modal.session.status === 'live' || (minutesUntilStart(modal.session) !== null && minutesUntilStart(modal.session) <= (bufferSettings.instructor_start_buffer || 10))} />
          ) : modal?.type === 'batch' ? (
            <BatchList items={modal.items} />
          ) : modal?.type === 'session' ? (
            <SessionList items={modal.items} onClick={(s) => setModal({ scope: 'session-detail', title: s.batch_name || s.title, description: `${formatDate(s.scheduled_date)} · ${s.scheduled_time} · ${s.duration_minutes}m`, type: 'session-detail', session: s, loading: false })} />
          ) : modal?.type === 'rating' ? (
            <RatingList items={modal.items} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Learner class-safety brief (no contact details ever shown) */}
      <Dialog open={!!brief} onOpenChange={(v) => !v && setBrief(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="learner-brief-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" />{brief?.name || 'Learner'}</DialogTitle>
            <DialogDescription>Class safety brief</DialogDescription>
          </DialogHeader>
          {brief?.loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : brief?.error ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="brief-error">{brief.error}</p>
          ) : (
            <LearnerBrief data={brief?.data} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BatchList({ items }) {
  if (!items?.length) return <p className="text-center py-8 text-muted-foreground">No batches yet</p>;
  return (
    <div className="space-y-2">
      {items.map((b) => (
        <div key={b.id} className="p-3 rounded-xl border" data-testid={`batch-item-${b.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{b.name}</p>
              <p className="text-xs text-muted-foreground">
                {b.batch_type === 'one_on_one' ? '1:1 batch' : 'Group batch'}
                {typeof b.current_enrollment === 'number' && ` · ${b.current_enrollment} enrolled`}
                {b.status && b.status !== 'active' && ` · ${b.status}`}
              </p>
            </div>
            {b.timezone && <Badge variant="outline" className="text-[10px]">{b.timezone}</Badge>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SessionList({ items, onClick }) {
  if (!items?.length) return <p className="text-center py-8 text-muted-foreground">None</p>;
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <button key={s.id} type="button" onClick={() => onClick?.(s)} className="w-full text-left p-3 rounded-xl border hover:bg-muted/20 transition-colors" data-testid={`session-item-${s.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{s.batch_name || s.title}</p>
              <p className="text-xs text-muted-foreground">{formatDate(s.scheduled_date)} · {s.scheduled_time} · {s.duration_minutes}m</p>
            </div>
            <Badge variant="outline" className={`text-[10px] ${getStatusColor(s.status)}`}>{s.status}</Badge>
          </div>
        </button>
      ))}
    </div>
  );
}

function RatingList({ items }) {
  if (!items?.length) return <p className="text-center py-8 text-muted-foreground">No ratings yet</p>;
  return (
    <div className="space-y-2">
      {items.map((r, idx) => (
        <div key={r.id || idx} className="p-3 rounded-xl border" data-testid={`rating-detail-${idx}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{r.batch_name}</p>
              {r.scheduled_date && <p className="text-xs text-muted-foreground">{formatDate(r.scheduled_date)}</p>}
            </div>
            <span className="text-sm font-semibold">{r.rating}/10</span>
          </div>
          {r.comments && <p className="mt-2 text-xs italic text-muted-foreground">"{r.comments}"</p>}
        </div>
      ))}
    </div>
  );
}

function LearnerBrief({ data }) {
  if (!data) return null;
  if (!data.onboarded || !data.brief) {
    return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="brief-empty">No coach profile yet for this learner.</p>;
  }
  const b = data.brief;
  return (
    <div className="space-y-3 text-sm" data-testid="learner-brief-content">
      <div className="flex flex-wrap gap-2">
        {b.week && <Badge className="bg-primary/10 text-primary border-0">Week {b.week}</Badge>}
        {b.trimester && <Badge variant="outline">Trimester {b.trimester}</Badge>}
        {b.risk_level === 'high' && <Badge className="bg-rose-600 text-white border-0">High risk</Badge>}
        {b.plan_mode && b.plan_mode !== 'normal' && <Badge variant="outline" className="capitalize">{b.plan_mode.replace('_', ' ')}</Badge>}
        {b.ivf_iui && <Badge variant="outline">IVF/IUI</Badge>}
      </div>

      {b.waiting_for_first_scan && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">Awaiting first scan — keep everything very gentle.</p>
      )}
      {b.last_scan_note && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">{b.last_scan_note}</p>
      )}

      {Array.isArray(b.avoid_if) && b.avoid_if.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
            <ShieldAlert className={`h-3.5 w-3.5 ${b.has_hard_restriction ? 'text-rose-600' : 'text-amber-600'}`} />Safety notes
          </p>
          <ul className="space-y-1">
            {b.avoid_if.map((a, i) => (
              <li key={i} className="text-xs rounded-md border px-2.5 py-1.5">
                <span className="font-medium">{a.condition}:</span> <span className="text-muted-foreground">{a.caution}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(!b.avoid_if || b.avoid_if.length === 0) && (
        <p className="text-xs text-muted-foreground">No specific conditions reported — general prenatal care applies.</p>
      )}

      {b.recent_focus && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recent focus</p>
          <p className="text-xs italic text-foreground/80">"{b.recent_focus}"</p>
        </div>
      )}

      {b.next_appointment && (
        <p className="text-xs text-muted-foreground">Next appointment: {b.next_appointment}</p>
      )}

      <p className="text-[10px] text-muted-foreground border-t pt-2">Confidential — for this class only. No contact details are shared with instructors.</p>
    </div>
  );
}

function SessionDetail({ session: s, onStart, canStart, onLearnerClick, canViewBrief }) {
  if (!s) return null;
  return (
    <div className="space-y-4" data-testid="session-detail-panel">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-muted-foreground">Date:</span> {formatDate(s.scheduled_date)}</div>
        <div><span className="text-muted-foreground">Time:</span> {s.scheduled_time}</div>
        <div><span className="text-muted-foreground">Duration:</span> {s.duration_minutes} min</div>
        <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className={`text-[10px] ${getStatusColor(s.status)}`}>{s.status}</Badge></div>
        <div className="col-span-2"><span className="text-muted-foreground">Batch:</span> {s.batch_name || '—'}</div>
        {s.zoom_meeting_id && <div className="col-span-2"><span className="text-muted-foreground">Zoom meeting:</span> {s.zoom_meeting_id}</div>}
      </div>

      {Array.isArray(s.booked_learners_info) && s.booked_learners_info.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Booked learners ({s.booked_learners_info.length})</p>
          {canViewBrief && <p className="text-[11px] text-muted-foreground mb-1">Tap a learner to see their class safety brief.</p>}
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {s.booked_learners_info.map((l, i) => {
              const clickable = canViewBrief && l.id && onLearnerClick;
              return (
                <button key={i} type="button" disabled={!clickable}
                  onClick={() => clickable && onLearnerClick(l.id, l.name)}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded border text-left transition-colors ${clickable ? 'hover:bg-muted cursor-pointer' : 'cursor-default'}`}
                  data-testid={`learner-row-${i}`}>
                  <span>{l.name || 'Learner'}</span>
                  <div className="flex gap-1 items-center">
                    {l.trimester && <Badge variant="outline" className="text-[10px]">T{l.trimester}</Badge>}
                    {l.risk_level === 'high' && <Badge className="bg-rose-600 text-white border-0 text-[10px]">High risk</Badge>}
                    {clickable && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(s.status === 'upcoming' || s.status === 'live') && (
        <Button onClick={onStart} disabled={s.status === 'upcoming' && !canStart} className="w-full" data-testid="modal-start-class-btn">
          <Video className="h-4 w-4 mr-1" />
          {s.status === 'live' ? 'Rejoin Class' : canStart ? 'Start Class' : 'Not yet (wait for buffer)'}
        </Button>
      )}
    </div>
  );
}
